import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useRef, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import CssBaseline from '@mui/material/CssBaseline';
import NavBar from './layouts/NavBar';
import AlternateNavBarXp from './layouts/AlternateNavBarXp';
import PrivateRoute from './components/PrivateRoute';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import SignUpBusiness from './pages/SignUpBusiness';
import SignUpDistributor from './pages/SignUpDistributor';
import Login from './pages/Login';
import CreateProduct from './pages/CreateProduct';
import ProductPage from './pages/ProductPage';
import DistributorPage from './pages/DistributorPage';
import MyConnections from './pages/MyConnections';
import MyProducts from './pages/MyProducts';
import ProfilePage from './pages/ProfilePage';
import AboutUs from './pages/AboutUs';
import PricesAndPlans from './pages/PricesAndPlans';
import PaymentPage from './pages/PaymentPage';
import EditPlanPage from './pages/EditPlanPage';
import AdminDashboard from './pages/AdminDashboard';
import withModeratorAuth from './components/routes/withModeratorAuth';
import withDistributorAuth from './components/routes/withDistributorAuth';
import ModeratorDashboard from './pages/ModeratorDashboard';
import menuTheme from './menuTheme';
import theme from './theme';
import FavoriteProducts from './pages/FavoriteProducts';
import MyCombos from './pages/MyCombos'
import CreateCombo from './pages/CreateCombo';
import ComboPage from './pages/ComboPage';
import ContactPage from './pages/ContactPage';
import alternateTheme from './alternateTheme';
import withAdminAuth from './components/routes/withAdminAuth';
import VerificationPending from './pages/VerificationPending';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SignUpPage from './pages/SignUpPage';
import '@fontsource/roboto/400.css'; // Regular weight
import '@fontsource/roboto/700.css'; // Bold weight
import Footer from './layouts/Footer';
import { Box } from '@mui/material';
import SubUserDashboard from './pages/SubUserDashboard';
import SubUsersPage from './pages/SubUsersPage';
import {auth} from './utils/auth';
import { SnackbarProvider } from './contexts/SnackbarContext';
import TermsAndServices from './pages/TermsAndServices';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Sitemap from './pages/Sitemap';

const ProtectedAdminDashboard = withAdminAuth(AdminDashboard);
const ProtectedModeratorDashboard = withModeratorAuth(ModeratorDashboard);
const ProtectedAdminPlanEdit = withAdminAuth(EditPlanPage);
const ProtectedCreateProduct = withDistributorAuth(CreateProduct);
const ProtectedMyProducts = withDistributorAuth(MyProducts);
const ProtectedSubUsers = withDistributorAuth(SubUsersPage);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {

  return (
    <SnackbarProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavBar />
            <Box sx={{ flex: 1 }}>
              <AppContent />
            </Box>
            <Footer />
          </Box>
        </BrowserRouter>
      </ThemeProvider>
    </SnackbarProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isPaymentPage = location.pathname === '/payment';
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>

          <Route path="/s" element={
            <ThemeProvider theme={menuTheme}>
              <LandingPage />
            </ThemeProvider>
          } />
          <Route path="/" element={<HomePage />} />
          <Route path="/signup-business" element={
              <ThemeProvider theme={alternateTheme}>
                <SignUpBusiness />
              </ThemeProvider>
          } />
          <Route path="/signup-distributor" element={
              <ThemeProvider theme={alternateTheme}>
                <SignUpDistributor />
              </ThemeProvider>
          } />
          <Route path="/terms-and-services" element={
              <ThemeProvider theme={menuTheme}>
                <TermsAndServices />
              </ThemeProvider>
          } />
          <Route path="/privacy-policy" element={
              <ThemeProvider theme={menuTheme}>
                <PrivacyPolicy />
              </ThemeProvider>
          } />
          <Route path="/signup" element={
              <ThemeProvider theme={alternateTheme}>
                <SignUpPage />
              </ThemeProvider>
          } />
          <Route path="/login" element={
              <ThemeProvider theme={alternateTheme}>
                <Login />
              </ThemeProvider>
          } />
          <Route path="/sub-user-dashboard" element={
            <PrivateRoute requireSubUser>
              <ThemeProvider theme={menuTheme}>
              <SubUserDashboard />
              </ThemeProvider>
            </PrivateRoute>
          } />

           {/* Sub-users routes - only for distributors */}
        {auth.isAuthenticated && auth.getUserType() === 'distributor' && (
          <>
            <Route path="/sub-users" element={
              <ThemeProvider theme={menuTheme}>
              <ProtectedSubUsers />
              </ThemeProvider>
              } />
          </>
        )}
          <Route path="/about" element={
            <ThemeProvider theme={alternateTheme}>
              <AboutUs />
            </ThemeProvider>
          } />
          
          <Route path="/product/:id" element={<ProductPage />} />
          
          <Route path="/distributor/:id" element={<DistributorPage />} />
          {/* <Route path="/combo/:id" element={<ComboPage />} /> */}
          <Route path="/my-connections" element={
            <PrivateRoute requireBusiness>
              <MyConnections />
            </PrivateRoute>
          } />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/my-products" element={
            <PrivateRoute requireDistributor>
              <ThemeProvider theme={menuTheme}>
              <ProtectedMyProducts />
              </ThemeProvider>
            </PrivateRoute>
          } />
          {/* <Route path="/my-combos" element={
            <PrivateRoute requireDistributor>
              <ThemeProvider theme={menuTheme}>
              <MyCombos />
              </ThemeProvider>
            </PrivateRoute>
          } /> */}
          <Route path="/create-product" element={
            <PrivateRoute requireDistributor>
              <ThemeProvider theme={menuTheme}>
              <ProtectedCreateProduct />
              </ThemeProvider>
            </PrivateRoute>
          } />
          {/* <Route path="/create-combo" element={
            <PrivateRoute requireDistributor>
              <ThemeProvider theme={menuTheme}>
              <CreateCombo />
              </ThemeProvider>
            </PrivateRoute>
          } /> */}
          <Route path="/profile" element={
            <PrivateRoute>
              <ThemeProvider theme={menuTheme}>
              <ProfilePage />
              </ThemeProvider>
            </PrivateRoute>
          } />
          
          <Route path="/contact" element={
            
            <ThemeProvider theme={alternateTheme}>
              <ContactPage />
            </ThemeProvider>
          } />
          <Route path="/favorite-products" element={
            <PrivateRoute>
            <FavoriteProducts />
            </PrivateRoute>
            } />
          <Route path="/pricing" element={
            <ThemeProvider theme={alternateTheme}>
              <PricesAndPlans />
            </ThemeProvider>
          } />
          <Route 
            path="/plans/:planId/edit" 
            element={
            <PrivateRoute requireAdmin>
            <ProtectedAdminPlanEdit />
            </PrivateRoute>
            } 
            sensitive = {true}         />

          
          <Route path="/admin/*" element={
            <PrivateRoute requireAdmin>
              <ProtectedAdminDashboard />
            </PrivateRoute>
          } />
          <Route path="/moderator/*" element={
            <PrivateRoute requireModerator>
              <ProtectedModeratorDashboard />
            </PrivateRoute>
          } />
          <Route path="/verification-pending" element={
            <ThemeProvider theme={menuTheme}>
              <VerificationPending />
            </ThemeProvider>
          } />
          <Route path="/verify-email/:token" element={
            <ThemeProvider theme={menuTheme}>
              <VerifyEmail />
            </ThemeProvider>
          } />
          <Route path="/forgot-password" element={
            <ThemeProvider theme={menuTheme}>
              <ForgotPassword />
            </ThemeProvider>
          } />
          <Route path="/reset-password/:token" element={
            <ThemeProvider theme={menuTheme}>
              <ResetPassword />
            </ThemeProvider>
          } />
          <Route path="/sitemap" element={
            <ThemeProvider theme={menuTheme}>
              <Sitemap />
            </ThemeProvider>
          } />
        </Routes>
      </AnimatePresence>
    </>
  );
}

export default App;
